import { gdSlsReportError } from "@/utils/gdSlsRum";
import TrackerEvent, { SimpleEvent } from "../utils/event";
import { PAGE_CHANGED } from "./shared/variable";

const defaultOption = {
  uniqueKey: "data-unique-id",
  exposeKey: "data-gd-expose",
  clickKey: "data-gd-click",
  exposeThreshold: 0.6,
  exposeTime: 1500,
  customExposeTimeKey: "data-expose-time",
  eventNameKey: null, // 默认和 exposeKey， clickKey 一致
};
/**
 * 埋点自动采集触发器
 * 主要包含点击和曝光的埋点
 */
export default class AutoTrigger {
  /**
   * 初始化
   * @param {Object} opt
   * @param {string} opt.uniqueKey 唯一属性key，避免重复曝光采集用的
   * @param {string} opt.exposeKey 具备这个属性才会触发曝光
   * @param {number} opt.exposeThreshold 曝光多少才算曝光的值 [0-1]
   * @param {number} opt.exposeTime 曝光时长 曝光多少时间才算曝光
   * @param {string} opt.clickKey 具备这个属性才会点击触发
   * @param {string} opt.customExposeTimeKey 可对元素自定义曝光时长的属性
   * @param {string|null} opt.eventNameKey 自定义事件名属性
   * @param {Array<(event: SimpleEvent)=>any>} opt.extends 扩展其他类型自动触发事件
   * @returns
   */
  constructor(opt = {}) {
    // 服务端情况下不执行
    if (typeof window === "undefined") return;

    try {
      const option = (this.option = Object.assign({}, defaultOption, opt));

      const {
        uniqueKey,
        exposeKey,
        clickKey,
        exposeThreshold,
        exposeTime,
        customExposeTimeKey,
        eventNameKey,
      } = option;

      const event = (this.event = new SimpleEvent());

      const method = initExpose(
        exposeKey,
        exposeTime,
        exposeThreshold,
        customExposeTimeKey,
        uniqueKey,
        eventNameKey,
        event
      );

      this.clearExposeUniqueList = method.clearUniqueList;

      initClick(clickKey, eventNameKey, event);

      // 初始化状态, 表明初始化成功
      this.$ready = true;
    } catch (error) {
      gdSlsReportError(new Error("auto trigger init fail: " + error?.message));
    }

    function initExpose(
      exposeKey,
      exposeTime,
      exposeThreshold,
      customExposeTimeKey,
      uniqueKey,
      eventNameKey,
      event
    ) {
      // 选择需要观察变动的节点
      const targetNode = document.body;
      // 唯一数组， 页面切换时需要清空
      const uniqueList = new Set();
      let cacheCoveredDomList = [];
      // 页面切换时需要清空
      TrackerEvent.on(PAGE_CHANGED, () => uniqueList.clear());

      // 创建expose监听器
      const intersection = new IntersectionObserver(
        function (entries, observer) {
          try {
            for (let i = 0; i < entries.length; i++) {
              const entrie = entries[i];
              const target = entrie.target;

              if (entrie.intersectionRatio <= exposeThreshold) {
                // 移除曝光时间标志，下次需要重新计时
                target.removeAttribute("data-expose-time-ok");
                continue;
              }

              // 当前只考虑采集详情页的弹框遮挡，其他暂不考虑
              // 当有遮挡时，不计入曝光（主要解决采集详情页弹框时，会继续上报背后遮挡的瀑布流曝光）
              if (
                target.hasAttribute("data-check-cover") &&
                isCovered(target)
              ) {
                observer.unobserve(target);
                cacheCoveredDomList.push(target);
                continue;
              }

              // 曝光时长
              var elementExposeTime = +(
                target.getAttribute(customExposeTimeKey) || exposeTime
              );
              // 曝光时间到了或者定制的曝光时间为0
              if (
                target.getAttribute("data-expose-time-ok") ||
                exposeTime === 0
              ) {
                const uniqueValue = target.getAttribute(uniqueKey);
                if (!(uniqueValue && uniqueList.has(uniqueValue))) {
                  const eventName =
                    target.getAttribute(eventNameKey || exposeKey) || "expose";
                  // 触发埋点
                  event.trigger(eventName, target);
                  target.removeAttribute("data-expose-time-ok");
                  // 唯一性避免重复曝光
                  if (target.getAttribute(uniqueKey)) {
                    uniqueList.add(target.getAttribute(uniqueKey));
                  }
                }
              } else {
                setTimeout(function () {
                  // 判断节点是否还在页面中
                  if (document.body.contains(target)) {
                    // 曝光时间到了,重新监听
                    target.setAttribute("data-expose-time-ok", 1);
                    observer.observe(target);
                  }
                }, elementExposeTime);
              }

              observer.unobserve(target);
            }
          } catch (error) {
            gdSlsReportError(error);
          }
        },
        {
          threshold: exposeThreshold,
        }
      );

      // 先遍历添加需要曝光的节点
      addExposeObserver(
        intersection,
        getExposeTrackerNode(targetNode, exposeKey)
      );

      // 观察器的配置（需要观察什么变动）
      const config = { attributes: true, childList: true, subtree: true };

      // 当观察到变动时执行的回调函数
      var mutationCallback = function (mutationsList, observer) {
        try {
          // 遍历节点，判断是否需要添加曝光监听
          for (var i = 0; i < mutationsList.length; i++) {
            var mutation = mutationsList[i];

            if (mutation.type === "childList") {
              // console.log('dom changed', mutation.addedNodes);
              addExposeObserver(intersection, mutation.addedNodes);
              removeExposeObserver(intersection, mutation.removedNodes);

              // cover element check;
              for (let i = 0; i < mutation.removedNodes.length; i++) {
                const element = mutation.removedNodes[i];
                if (element instanceof HTMLElement) {
                  const coverElement =
                    element.getElementsByClassName("pinDetailModal");
                  if (coverElement.length) {
                    for (let i = 0; i < cacheCoveredDomList.length; i++) {
                      const targetNode = cacheCoveredDomList[i];
                      if (document.body.contains(targetNode)) {
                        intersection.observe(targetNode);
                      }
                    }

                    cacheCoveredDomList = [];
                  }
                }
              }
              continue;
            }
            if (mutation.type === "attributes") {
              // console.log('attr changed', mutation);
              if (mutation.attributeName === uniqueKey) {
                if (
                  mutation.oldValue !==
                  mutation.target.getAttribute(mutation.attributeName)
                ) {
                  addExposeObserver(intersection, mutation.target);
                }
              }
            }
          }
        } catch (error) {
          gdSlsReportError(error);
        }
      };

      // 创建一个观察器实例并传入回调函数
      var mutationObserver = new MutationObserver(mutationCallback);

      // 以上述配置开始观察目标节点
      mutationObserver.observe(targetNode, config);

      /**
       * 获取需要曝光埋点的节点
       * @param { HTMLElement }node  根节点
       * @return {HTMLElement[]} 节点集合
       */
      function getExposeTrackerNode(node, exposeKey) {
        const filterNodeList = [];

        if (node instanceof HTMLElement) {
          if (node.hasAttribute(exposeKey)) {
            filterNodeList.push(node);
          }

          const query = node.querySelectorAll(`[${exposeKey}]`);
          for (var i = 0; i < query.length; i++) {
            const el = query[i];
            filterNodeList.push(el);
          }
        }

        return filterNodeList;
      }

      function addExposeObserver(intersection, nodes) {
        ExposeObserver(intersection, nodes);
      }

      function removeExposeObserver(intersection, nodes) {
        ExposeObserver(intersection, nodes, true);
      }
      /**
       * 添加或者移除曝光监听
       * @param { IntersectionObserver } intersection
       * @param { HTMLElement[] } nodes
       * @param { Boolean } unobserve
       */
      function ExposeObserver(intersection, nodes, unobserve) {
        if (nodes instanceof HTMLElement) {
          nodes = [nodes];
        }

        let filterNodes = [];
        if (nodes.length) {
          for (var i = 0; i < nodes.length; i++) {
            var node = nodes[i];
            filterNodes = filterNodes.concat(
              getExposeTrackerNode(node, exposeKey)
            );
          }
        }

        for (let i = 0; i < filterNodes.length; i++) {
          let node = filterNodes[i];
          if (node instanceof HTMLElement) {
            if (unobserve) {
              intersection.unobserve(node);
            } else {
              // console.log('add exposed', node);
              intersection.observe(node);
            }
          }
        }
      }

      /**
       * dom元素是否被其他元素覆盖
       * @param {HTMLElement} target
       */
      function isCovered(target) {
        let document = target.ownerDocument;
        // 被采集详情弹窗遮挡
        const coverTarget = document.querySelector(".pinDetailModal");
        let { x, y, width, height } = target.getBoundingClientRect();
        let elements = [
          document.elementFromPoint(x, y),
          document.elementFromPoint(x + width, y),
          document.elementFromPoint(x, y + height),
          document.elementFromPoint(x + width, y + height),
        ];

        return elements
          .filter((el) => el !== null)
          .some((el) => {
            if (
              // 遮挡元素与监测元素不存在父子包含关系、或不相等
              el.contains(target) ||
              target.contains(el) ||
              target === el
            ) {
              return false;
            }

            // 遮挡元素只有为采集详情页弹框时，才算被遮挡
            if (
              coverTarget &&
              (coverTarget.contains(el) ||
                el.contains(coverTarget) ||
                el === coverTarget)
            ) {
              return true;
            }

            return false;
          });
      }

      return {
        clearUniqueList: () => uniqueList.clear(),
      };
    }

    function initClick(clickKey, eventNameKey, event) {
      document.body.addEventListener(
        "click",
        function (e) {
          var node = e.target;
          while (node !== document.body) {
            // 遍历节点，找到需要埋点上报的节点
            if (node.hasAttribute(clickKey)) {
              // 获取事件名
              var eventName =
                node.getAttribute(eventNameKey || clickKey) || "contentClick";
              // 执行对应的埋点上报方法
              // 触发埋点
              event.trigger(eventName, node);
              return;
            }
            node = node.parentElement;
          }
        },
        {
          // 避免元素阻止冒泡导致无法获取
          capture: true,
        }
      );
    }
  }

  on(...arg) {
    this.event.on(...arg);
  }

  off(...arg) {
    this.event.off(...arg);
  }

  trigger(...arg) {
    this.event.trigger(...arg);
  }
}
